import { useState } from 'react';
import { useApolloClient, useQuery, useMutation } from '@apollo/client';
import { Agent, AgentType } from '../../../../types';
import { useNotificationService } from '../../../Notification';
import { useAgentApi } from '../../../../contexts/ApiContext';
import { useTaskManager } from '../../../../contexts/TaskManagerContext';
import { GET_AGENTS, TOGGLE_AGENT_HIDDEN } from '../../../../graphql/queries';
import { GetAgentsQuery, ToggleAgentHiddenMutation } from '../../../../gql/graphql';
import { useAdminTranslation, useCommonTranslation } from '../../../../modules/i18n';

// Types for API payloads matching verba_v2 backend
interface CreateAgentFolderPayload {
  folder_id: string;
}

interface AgentToolPayload {
  tool_id: string;
  is_active: boolean;
  config?: any;
}

interface CreateAgentPayload {
  name: string;
  agent_type?: AgentType;
  agent_folders: CreateAgentFolderPayload[];
  language_id: string;
  tools?: AgentToolPayload[];
  workflow_definition?: Record<string, unknown>;
}

interface UpdateAgentPayload {
  name: string;
  agent_type?: AgentType;
  agent_folders: CreateAgentFolderPayload[];
  language_id: string;
  tools?: AgentToolPayload[];
  workflow_definition?: Record<string, unknown>;
}

interface UseAgentsReturn {
  // State
  agents: Agent[];
  loading: boolean;
  error: any;
  showAgentModal: boolean;
  editingAgent: Agent | null;
  selectedAgent: Agent | null;
  showAgentDetailModal: boolean;
  showHidden: boolean;

  // Actions
  handleCreateAgent: () => void;
  handleEditAgent: (agent: Agent) => void;
  handleDeleteAgent: (agentId: string) => void;
  handleViewAgentDetail: (agent: Agent) => void;
  handleCloseModal: () => void;
  handleCloseDetailModal: () => void;
  handleSaveAgent: (agentData: Partial<Agent> & { folderIds?: string[] }) => Promise<void>;
  handleToggleHidden: (agentId: string) => Promise<void>;
  setShowHidden: (show: boolean) => void;
  refetchAgents: () => void;
}

export const useAgents = (): UseAgentsReturn => {
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [showAgentDetailModal, setShowAgentDetailModal] = useState(false);
  const [showHidden, setShowHidden] = useState(false);

  const { showSuccess, showError } = useNotificationService();
  const agentApi = useAgentApi();
  const apolloClient = useApolloClient();
  const { trackedTasks, stopTracking } = useTaskManager();
  const { t } = useAdminTranslation();
  const { t: tCommon } = useCommonTranslation();

  // Use GraphQL query to fetch agents (NestJS backend)
  const { data, loading, error, refetch } = useQuery<GetAgentsQuery>(GET_AGENTS);

  // Mutation for toggling hidden status
  const [toggleHiddenMutation] = useMutation<ToggleAgentHiddenMutation>(TOGGLE_AGENT_HIDDEN);

  // Transform GraphQL data to match our Agent interface
  const allAgents: Agent[] =
    data?.findManyAgents?.map((agent) => {
      return {
        id: agent.id,
        name: agent.name,
        agentType: (agent.agentType as AgentType) || 'KG_RAG',
        knowledgeGraphId: agent.knowledgeGraphId || undefined,
        knowledgeGraph: agent.knowledgeGraph
          ? {
            // Full KG object when included
            id: agent.knowledgeGraph.id,
            namespace: agent.knowledgeGraph.namespace,
            version: agent.knowledgeGraph.version,
            status: agent.knowledgeGraph.status
          }
          : undefined,
        workflowId: agent.workflowId || undefined,
        workflow: agent.workflow
          ? {
            id: agent.workflow.id,
            name: agent.workflow.name,
            definition: agent.workflow.definition as Record<string, unknown>,
            version: '1.0.0'
          }
          : undefined,
        userId: agent.userId,
        languageId: agent.languageId,
        status: agent.status as 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'ERROR',
        enabled: true,
        hidden: agent.hidden ?? false,
        createdAt: agent.createdAt,
        updatedAt: agent.updatedAt,
        language: agent.language
          ? {
            id: agent.language.id,
            name: agent.language.name,
            code: agent.language.code,
            createdAt: agent.language.createdAt,
            updatedAt: agent.language.updatedAt
          }
          : undefined,
        agentFolders: agent.agentFolders?.map((af) => ({ agentId: af.agentId, folderId: af.folderId })) || [],
        tools: []
      };
    }) || [];

  // Filter hidden agents based on showHidden state
  const agents = showHidden ? allAgents : allAgents.filter((agent) => !agent.hidden);

  const handleCreateAgent = () => {
    setEditingAgent(null);
    setShowAgentModal(true);
  };

  const handleEditAgent = (agent: Agent) => {
    setEditingAgent(agent);
    setShowAgentModal(true);
  };

  const handleDeleteAgent = async (agentId: string) => {
    // Snapshot the agent's namespace BEFORE delete so we can clean up
    // client-side task trackers — after Apollo cache eviction the row is gone.
    const deletedAgent = allAgents.find((a) => a.id === agentId);
    const deletedNamespace = deletedAgent?.knowledgeGraph?.namespace;

    try {
      const result: any = await agentApi.deleteAgent(agentId);

      // Compose toast from the structured backend payload:
      //   { ok, agent_id, already_deleted, cancelled_task_ids[],
      //     cancelled_tasks[], cleanup_enqueued_job_id, ... }
      const cancelledCount = Array.isArray(result?.cancelled_task_ids) ? result.cancelled_task_ids.length : 0;
      const cleanupJobId: string | null = result?.cleanup_enqueued_job_id ?? null;

      const parts: string[] = [t('content.agents.operations.deleteSuccess')];
      if (cancelledCount > 0) {
        parts.push(t('content.agents.operations.deleteCancelledTasks', { count: cancelledCount } as any));
      }
      if (cleanupJobId) {
        parts.push(t('content.agents.operations.deleteCleanupQueued'));
      }
      showSuccess(tCommon('status.success'), parts.join(' '));

      // Stop polling trackers for tasks tied to the deleted agent's namespace —
      // pods detect cancellation within ~30 s; UI shouldn't keep hammering.
      if (deletedNamespace) {
        trackedTasks
          .filter((task) => task.namespace === deletedNamespace)
          .forEach((task) => stopTracking(task.taskId));
      }

      // Evict the Agent from Apollo cache so dependent queries
      // (GET_AGENT_BY_ID, chat rooms, etc.) drop the dangling ref, then
      // refetch the list as the cheap source of truth.
      apolloClient.cache.evict({ id: apolloClient.cache.identify({ __typename: 'Agent', id: agentId }) });
      apolloClient.cache.gc();
      refetch();
    } catch (error) {
      console.error('Error deleting agent:', error);
      const msg = error instanceof Error && error.message ? error.message : t('content.agents.operations.deleteError');
      showError(tCommon('status.error'), msg);
    }
  };

  const handleViewAgentDetail = (agent: Agent) => {
    setSelectedAgent(agent);
    setShowAgentDetailModal(true);
  };

  const handleCloseModal = () => {
    setShowAgentModal(false);
    setEditingAgent(null);
  };

  const handleCloseDetailModal = () => {
    setShowAgentDetailModal(false);
    setSelectedAgent(null);
  };

  const handleSaveAgent = async (agentData: Partial<Agent> & { folderIds?: string[] }) => {
    try {
      if (editingAgent) {
        // Update existing agent
        const updatePayload: UpdateAgentPayload = {
          name: agentData.name || editingAgent.name,
          agent_type: agentData.agentType || editingAgent.agentType,
          language_id: agentData.languageId || editingAgent.languageId,
          agent_folders: (agentData.folderIds || agentData.agentFolders?.map((af) => af.folderId) || []).map((folderId) => ({ folder_id: folderId })),
          tools:
            agentData.tools?.map((tool) => ({
              tool_id: tool.toolId,
              is_active: tool.isActive || true,
              config: tool.config
            })) || []
        };

        await agentApi.updateAgent(editingAgent.id, updatePayload);

        showSuccess(tCommon('status.success'), t('content.agents.operations.updateSuccess'));
      } else {
        // Create new agent using modern orchestration endpoint
        const createPayload: CreateAgentPayload = {
          name: agentData.name || '',
          agent_type: agentData.agentType || 'KG_RAG',
          language_id: agentData.languageId || '',
          agent_folders: (agentData.folderIds || agentData.agentFolders?.map((af) => af.folderId) || []).map((folderId) => ({ folder_id: folderId })),
          tools:
            agentData.tools?.map((tool) => ({
              tool_id: tool.toolId,
              is_active: tool.isActive || true,
              config: tool.config
            })) || []
        };

        await agentApi.createAgent(createPayload);

        showSuccess(tCommon('status.success'), t('content.agents.operations.createSuccess'));
      }

      // Refetch the agents list and close modal
      refetch();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving agent:', error);
      showError(tCommon('status.error'), editingAgent ? t('content.agents.operations.updateError') : t('content.agents.operations.createError'));
    }
  };

  const handleToggleHidden = async (agentId: string) => {
    try {
      await toggleHiddenMutation({
        variables: { id: agentId }
      });
      showSuccess(tCommon('status.success'), t('content.agents.operations.toggleHiddenSuccess'));
      refetch();
    } catch (error) {
      console.error('Error toggling agent hidden status:', error);
      showError(tCommon('status.error'), t('content.agents.operations.toggleHiddenError'));
    }
  };

  const refetchAgents = () => {
    refetch();
  };

  return {
    // State
    agents,
    loading,
    error,
    showAgentModal,
    editingAgent,
    selectedAgent,
    showAgentDetailModal,
    showHidden,

    // Actions
    handleCreateAgent,
    handleEditAgent,
    handleDeleteAgent,
    handleViewAgentDetail,
    handleCloseModal,
    handleCloseDetailModal,
    handleSaveAgent,
    handleToggleHidden,
    setShowHidden,
    refetchAgents
  };
};
